export const MARKET_QUOTE_SCHEMA_VERSION = 1 as const;
export const MARKET_QUOTE_SOURCE = "yahoo-finance" as const;
export const MARKET_QUOTE_MAX_ITEMS = 25;
export const MARKET_QUOTE_MAX_TICKER_LENGTH = 32;
export const MARKET_QUOTE_HISTORY_MAX_SESSIONS = 7;
export const MARKET_QUOTE_HISTORY_MIN_SESSIONS = 2;
export const MARKET_INSTRUMENT_SEARCH_MAX_RESULTS = 8;
export const MARKET_INSTRUMENT_SEARCH_MAX_QUERY_LENGTH = 60;

export const MARKET_HINTS = [
  "AUTO",
  "US",
  "KRX",
  "KOSPI",
  "KOSDAQ",
] as const;

export type MarketHint = (typeof MARKET_HINTS)[number];

export interface MarketInstrumentSearchRequestV1 {
  readonly schemaVersion: typeof MARKET_QUOTE_SCHEMA_VERSION;
  readonly query: string;
  readonly market: "KRX";
  readonly limit?: number;
}

export interface MarketInstrumentSearchResultV1 {
  readonly canonicalTicker: string;
  readonly name: string;
  readonly exchange: "KOSPI" | "KOSDAQ";
  readonly assetKind: "stock" | "etf";
}

export interface MarketInstrumentSearchResponseV1 {
  readonly schemaVersion: typeof MARKET_QUOTE_SCHEMA_VERSION;
  readonly source: typeof MARKET_QUOTE_SOURCE;
  readonly query: string;
  readonly results: readonly MarketInstrumentSearchResultV1[];
}

export interface MarketQuoteLookupItemV1 {
  readonly requestId: string;
  readonly ticker: string;
  readonly marketHint: MarketHint;
  readonly targetCurrency?: string;
}

export interface MarketQuoteRequestV1 {
  readonly schemaVersion: typeof MARKET_QUOTE_SCHEMA_VERSION;
  readonly items: readonly MarketQuoteLookupItemV1[];
  /**
   * Registration-only metadata enrichment. Regular refreshes omit this to
   * avoid an additional provider lookup for already-saved Korean holdings.
   */
  readonly includeLocalizedNames?: boolean;
  /**
   * Optional bounded daily-close history. Regular refreshes omit this so the
   * existing small current-quote response remains unchanged.
   */
  readonly historySessionCount?: number;
}

export interface MarketQuoteHistorySessionV1 {
  readonly marketDate: string;
  readonly observedAt: string;
  readonly nativeClose: string;
  readonly close: string;
  readonly previousClose: string;
  readonly dailyChange: string;
  readonly dailyReturnPct: string;
}

export interface ResolvedMarketQuoteV1 {
  readonly status: "resolved";
  readonly requestId: string;
  readonly inputTicker: string;
  readonly canonicalTicker: string;
  readonly name?: string;
  readonly exchange: string;
  readonly market: "US" | "KRX" | "OTHER";
  readonly nativeCurrency: string;
  readonly quoteCurrency: string;
  readonly nativeCurrentPrice: string;
  readonly currentPrice: string;
  readonly previousClose: string;
  readonly dailyChange: string;
  readonly dailyReturnPct: string;
  readonly observedAt: string;
  readonly marketDate: string;
  readonly marketTimeZone: string;
  readonly source: typeof MARKET_QUOTE_SOURCE;
  readonly delayed: true;
  readonly historySessions?: readonly MarketQuoteHistorySessionV1[];
}

export interface NotFoundMarketQuoteV1 {
  readonly status: "not_found";
  readonly requestId: string;
  readonly inputTicker: string;
}

export interface AmbiguousMarketQuoteV1 {
  readonly status: "ambiguous";
  readonly requestId: string;
  readonly inputTicker: string;
  readonly candidates: readonly string[];
}

export interface CurrencyUnavailableMarketQuoteV1 {
  readonly status: "currency_unavailable";
  readonly requestId: string;
  readonly inputTicker: string;
  readonly canonicalTicker: string;
  readonly nativeCurrency: string;
  readonly targetCurrency: string;
}

export interface TemporarilyUnavailableMarketQuoteV1 {
  readonly status: "temporarily_unavailable";
  readonly requestId: string;
  readonly inputTicker: string;
  readonly retryAfterSeconds?: number;
}

export type MarketQuoteResultV1 =
  | ResolvedMarketQuoteV1
  | NotFoundMarketQuoteV1
  | AmbiguousMarketQuoteV1
  | CurrencyUnavailableMarketQuoteV1
  | TemporarilyUnavailableMarketQuoteV1;

export interface MarketQuoteResponseV1 {
  readonly schemaVersion: typeof MARKET_QUOTE_SCHEMA_VERSION;
  readonly source: typeof MARKET_QUOTE_SOURCE;
  readonly fetchedAt: string;
  readonly results: readonly MarketQuoteResultV1[];
}

export type MarketQuoteValidationResult<T> =
  | { readonly success: true; readonly data: T }
  | {
      readonly success: false;
      readonly code:
        | "invalid_body"
        | "invalid_request"
        | "resource_limit"
        | "unsupported_version";
      readonly message: string;
    };

const REQUEST_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/;
const TICKER_PATTERN = /^[A-Z0-9^][A-Z0-9.^=-]*$/;
const CURRENCY_PATTERN = /^[A-Z]{3}$/;
const DECIMAL_PATTERN = /^-?(?:0|[1-9]\d*)(?:\.\d+)?$/;
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

function isObject(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value)
  );
}

function hasOnlyKeys(
  value: Record<string, unknown>,
  keys: ReadonlySet<string>,
): boolean {
  return Object.keys(value).every((key) => keys.has(key));
}

function isRfc3339(value: unknown): value is string {
  if (typeof value !== "string") {
    return false;
  }
  const timestamp = Date.parse(value);
  return (
    Number.isFinite(timestamp) &&
    /(?:Z|[+-]\d{2}:\d{2})$/.test(value)
  );
}

function isDecimal(value: unknown): value is string {
  return typeof value === "string" && DECIMAL_PATTERN.test(value);
}

function isCurrency(value: unknown): value is string {
  return typeof value === "string" && CURRENCY_PATTERN.test(value);
}

function isRequestId(value: unknown): value is string {
  return typeof value === "string" && REQUEST_ID_PATTERN.test(value);
}

function isTicker(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value.length <= MARKET_QUOTE_MAX_TICKER_LENGTH &&
    TICKER_PATTERN.test(value)
  );
}

function normalizedSearchQuery(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }
  const query = value.normalize("NFKC").replace(/\s+/g, " ").trim();
  if (
    Array.from(query).length < 2 ||
    Array.from(query).length > MARKET_INSTRUMENT_SEARCH_MAX_QUERY_LENGTH ||
    !/[가-힣]/.test(query) ||
    Array.from(query).some((character) => {
      const code = character.charCodeAt(0);
      return code < 32 || code === 127;
    })
  ) {
    return null;
  }
  return query;
}

export function validateMarketInstrumentSearchRequestV1(
  value: unknown,
): MarketQuoteValidationResult<MarketInstrumentSearchRequestV1> {
  if (!isObject(value)) {
    return {
      success: false,
      code: "invalid_body",
      message: "종목 검색 요청은 JSON 객체여야 합니다.",
    };
  }
  if (
    !hasOnlyKeys(value, new Set(["schemaVersion", "query", "market", "limit"]))
  ) {
    return invalidRequest("지원하지 않는 종목 검색 필드가 포함되어 있습니다.");
  }
  if (value.schemaVersion !== MARKET_QUOTE_SCHEMA_VERSION) {
    return {
      success: false,
      code: "unsupported_version",
      message: "지원하지 않는 종목 검색 버전입니다.",
    };
  }
  const query = normalizedSearchQuery(value.query);
  if (!query) {
    return invalidRequest("한국 종목명은 한글을 포함해 2~60자로 입력해 주세요.");
  }
  if (value.market !== "KRX") {
    return invalidRequest("현재 종목명 검색은 한국 시장만 지원합니다.");
  }
  if (
    value.limit !== undefined &&
    (!Number.isInteger(value.limit) ||
      Number(value.limit) < 1 ||
      Number(value.limit) > MARKET_INSTRUMENT_SEARCH_MAX_RESULTS)
  ) {
    return invalidRequest(
      `종목 검색 결과는 1~${MARKET_INSTRUMENT_SEARCH_MAX_RESULTS}개만 요청할 수 있습니다.`,
    );
  }
  return {
    success: true,
    data: {
      schemaVersion: MARKET_QUOTE_SCHEMA_VERSION,
      query,
      market: "KRX",
      ...(value.limit === undefined ? {} : { limit: Number(value.limit) }),
    },
  };
}

function invalidRequest(
  message: string,
): MarketQuoteValidationResult<never> {
  return { success: false, code: "invalid_request", message };
}

export function validateMarketQuoteRequestV1(
  value: unknown,
): MarketQuoteValidationResult<MarketQuoteRequestV1> {
  if (!isObject(value)) {
    return {
      success: false,
      code: "invalid_body",
      message: "요청 본문은 JSON 객체여야 합니다.",
    };
  }
  if (
    !hasOnlyKeys(
      value,
      new Set([
        "schemaVersion",
        "items",
        "includeLocalizedNames",
        "historySessionCount",
      ]),
    )
  ) {
    return invalidRequest("지원하지 않는 요청 필드가 포함되어 있습니다.");
  }
  if (value.schemaVersion !== MARKET_QUOTE_SCHEMA_VERSION) {
    return {
      success: false,
      code: "unsupported_version",
      message: "지원하지 않는 시세 요청 버전입니다.",
    };
  }
  if (!Array.isArray(value.items) || value.items.length === 0) {
    return invalidRequest("조회할 티커를 한 개 이상 보내 주세요.");
  }
  if (value.items.length > MARKET_QUOTE_MAX_ITEMS) {
    return {
      success: false,
      code: "resource_limit",
      message: `한 번에 ${MARKET_QUOTE_MAX_ITEMS}개까지 조회할 수 있습니다.`,
    };
  }
  if (
    value.includeLocalizedNames !== undefined &&
    typeof value.includeLocalizedNames !== "boolean"
  ) {
    return invalidRequest(
      "현지 종목명 조회 여부는 boolean이어야 합니다.",
    );
  }
  if (
    value.historySessionCount !== undefined &&
    (
      !Number.isInteger(value.historySessionCount) ||
      Number(value.historySessionCount) <
        MARKET_QUOTE_HISTORY_MIN_SESSIONS ||
      Number(value.historySessionCount) >
        MARKET_QUOTE_HISTORY_MAX_SESSIONS
    )
  ) {
    return invalidRequest(
      `과거 시세는 ${MARKET_QUOTE_HISTORY_MIN_SESSIONS}~${MARKET_QUOTE_HISTORY_MAX_SESSIONS}개 거래 세션만 조회할 수 있습니다.`,
    );
  }

  const requestIds = new Set<string>();
  const items: MarketQuoteLookupItemV1[] = [];
  for (const item of value.items) {
    if (
      !isObject(item) ||
      !hasOnlyKeys(
        item,
        new Set([
          "requestId",
          "ticker",
          "marketHint",
          "targetCurrency",
        ]),
      )
    ) {
      return invalidRequest("시세 조회 항목 형식이 올바르지 않습니다.");
    }
    if (!isRequestId(item.requestId) || requestIds.has(item.requestId)) {
      return invalidRequest(
        "requestId는 항목마다 고유한 안전한 식별자여야 합니다.",
      );
    }
    if (!isTicker(item.ticker)) {
      return invalidRequest(
        "티커는 대문자 영문·숫자·점·하이픈 형식이어야 합니다.",
      );
    }
    if (
      typeof item.marketHint !== "string" ||
      !MARKET_HINTS.includes(item.marketHint as MarketHint)
    ) {
      return invalidRequest("지원하는 조회 시장을 선택해 주세요.");
    }
    if (
      item.targetCurrency !== undefined &&
      !isCurrency(item.targetCurrency)
    ) {
      return invalidRequest("목표 통화는 ISO 4217 대문자 3자리여야 합니다.");
    }
    requestIds.add(item.requestId);
    items.push({
      requestId: item.requestId,
      ticker: item.ticker,
      marketHint: item.marketHint as MarketHint,
      ...(item.targetCurrency
        ? { targetCurrency: item.targetCurrency }
        : {}),
    });
  }

  return {
    success: true,
    data: {
      schemaVersion: MARKET_QUOTE_SCHEMA_VERSION,
      items,
      ...(value.includeLocalizedNames === undefined
        ? {}
        : { includeLocalizedNames: value.includeLocalizedNames }),
      ...(value.historySessionCount === undefined
        ? {}
        : { historySessionCount: Number(value.historySessionCount) }),
    },
  };
}

function parseHistorySessions(
  value: unknown,
): readonly MarketQuoteHistorySessionV1[] | null {
  if (
    !Array.isArray(value) ||
    value.length < MARKET_QUOTE_HISTORY_MIN_SESSIONS ||
    value.length > MARKET_QUOTE_HISTORY_MAX_SESSIONS
  ) {
    return null;
  }
  let previousDate = "";
  let previousObservedAt = Number.NEGATIVE_INFINITY;
  const sessions: MarketQuoteHistorySessionV1[] = [];
  for (const session of value) {
    if (
      !isObject(session) ||
      !hasOnlyKeys(
        session,
        new Set([
          "marketDate",
          "observedAt",
          "nativeClose",
          "close",
          "previousClose",
          "dailyChange",
          "dailyReturnPct",
        ]),
      ) ||
      typeof session.marketDate !== "string" ||
      !ISO_DATE_PATTERN.test(session.marketDate) ||
      !isRfc3339(session.observedAt) ||
      !isDecimal(session.nativeClose) ||
      !isDecimal(session.close) ||
      !isDecimal(session.previousClose) ||
      !isDecimal(session.dailyChange) ||
      !isDecimal(session.dailyReturnPct)
    ) {
      return null;
    }
    const observedAt = Date.parse(session.observedAt);
    if (
      session.marketDate <= previousDate ||
      observedAt <= previousObservedAt
    ) {
      return null;
    }
    previousDate = session.marketDate;
    previousObservedAt = observedAt;
    sessions.push(session as unknown as MarketQuoteHistorySessionV1);
  }
  return sessions;
}

function validBaseResult(
  value: Record<string, unknown>,
): value is Record<string, unknown> & {
  requestId: string;
  inputTicker: string;
} {
  return isRequestId(value.requestId) && isTicker(value.inputTicker);
}

function parseMarketQuoteResultV1(
  value: unknown,
): MarketQuoteResultV1 | null {
  if (!isObject(value) || !validBaseResult(value)) {
    return null;
  }

  switch (value.status) {
    case "resolved": {
      const keys = new Set([
        "status",
        "requestId",
        "inputTicker",
        "canonicalTicker",
        "name",
        "exchange",
        "market",
        "nativeCurrency",
        "quoteCurrency",
        "nativeCurrentPrice",
        "currentPrice",
        "previousClose",
        "dailyChange",
        "dailyReturnPct",
        "observedAt",
        "marketDate",
        "marketTimeZone",
        "source",
        "delayed",
        "historySessions",
      ]);
      const historySessions =
        value.historySessions === undefined
          ? undefined
          : parseHistorySessions(value.historySessions);
      if (
        !hasOnlyKeys(value, keys) ||
        !isTicker(value.canonicalTicker) ||
        (value.name !== undefined &&
          (typeof value.name !== "string" || value.name.length > 160)) ||
        typeof value.exchange !== "string" ||
        value.exchange.length < 1 ||
        value.exchange.length > 64 ||
        !["US", "KRX", "OTHER"].includes(String(value.market)) ||
        !isCurrency(value.nativeCurrency) ||
        !isCurrency(value.quoteCurrency) ||
        !isDecimal(value.nativeCurrentPrice) ||
        !isDecimal(value.currentPrice) ||
        !isDecimal(value.previousClose) ||
        !isDecimal(value.dailyChange) ||
        !isDecimal(value.dailyReturnPct) ||
        !isRfc3339(value.observedAt) ||
        typeof value.marketDate !== "string" ||
        !ISO_DATE_PATTERN.test(value.marketDate) ||
        typeof value.marketTimeZone !== "string" ||
        value.marketTimeZone.length < 1 ||
        value.marketTimeZone.length > 64 ||
        value.source !== MARKET_QUOTE_SOURCE ||
        value.delayed !== true ||
        historySessions === null
      ) {
        return null;
      }
      return {
        ...(value as unknown as ResolvedMarketQuoteV1),
        ...(historySessions ? { historySessions } : {}),
      };
    }
    case "not_found":
      return hasOnlyKeys(
        value,
        new Set(["status", "requestId", "inputTicker"]),
      )
        ? (value as unknown as NotFoundMarketQuoteV1)
        : null;
    case "ambiguous":
      return hasOnlyKeys(
        value,
        new Set([
          "status",
          "requestId",
          "inputTicker",
          "candidates",
        ]),
      ) &&
        Array.isArray(value.candidates) &&
        value.candidates.length >= 2 &&
        value.candidates.every(isTicker)
        ? (value as unknown as AmbiguousMarketQuoteV1)
        : null;
    case "currency_unavailable":
      return hasOnlyKeys(
        value,
        new Set([
          "status",
          "requestId",
          "inputTicker",
          "canonicalTicker",
          "nativeCurrency",
          "targetCurrency",
        ]),
      ) &&
        isTicker(value.canonicalTicker) &&
        isCurrency(value.nativeCurrency) &&
        isCurrency(value.targetCurrency)
        ? (value as unknown as CurrencyUnavailableMarketQuoteV1)
        : null;
    case "temporarily_unavailable":
      return hasOnlyKeys(
        value,
        new Set([
          "status",
          "requestId",
          "inputTicker",
          "retryAfterSeconds",
        ]),
      ) &&
        (value.retryAfterSeconds === undefined ||
          (Number.isInteger(value.retryAfterSeconds) &&
            Number(value.retryAfterSeconds) > 0 &&
            Number(value.retryAfterSeconds) <= 3_600))
        ? (value as unknown as TemporarilyUnavailableMarketQuoteV1)
        : null;
    default:
      return null;
  }
}

export function validateMarketQuoteResponseV1(
  value: unknown,
): MarketQuoteValidationResult<MarketQuoteResponseV1> {
  if (
    !isObject(value) ||
    !hasOnlyKeys(
      value,
      new Set(["schemaVersion", "source", "fetchedAt", "results"]),
    )
  ) {
    return {
      success: false,
      code: "invalid_body",
      message: "시세 응답 형식이 올바르지 않습니다.",
    };
  }
  if (value.schemaVersion !== MARKET_QUOTE_SCHEMA_VERSION) {
    return {
      success: false,
      code: "unsupported_version",
      message: "지원하지 않는 시세 응답 버전입니다.",
    };
  }
  if (
    value.source !== MARKET_QUOTE_SOURCE ||
    !isRfc3339(value.fetchedAt) ||
    !Array.isArray(value.results) ||
    value.results.length > MARKET_QUOTE_MAX_ITEMS
  ) {
    return invalidRequest("시세 응답 값이 올바르지 않습니다.");
  }

  const results = value.results.map(parseMarketQuoteResultV1);
  if (results.some((result) => result === null)) {
    return invalidRequest("시세 응답 항목이 올바르지 않습니다.");
  }
  const requestIds = new Set(results.map((result) => result!.requestId));
  if (requestIds.size !== results.length) {
    return invalidRequest("시세 응답 requestId가 중복되었습니다.");
  }

  return {
    success: true,
    data: {
      schemaVersion: MARKET_QUOTE_SCHEMA_VERSION,
      source: MARKET_QUOTE_SOURCE,
      fetchedAt: value.fetchedAt,
      results: results as MarketQuoteResultV1[],
    },
  };
}

export function validateMarketInstrumentSearchResponseV1(
  value: unknown,
): MarketQuoteValidationResult<MarketInstrumentSearchResponseV1> {
  if (
    !isObject(value) ||
    !hasOnlyKeys(
      value,
      new Set(["schemaVersion", "source", "query", "results"]),
    )
  ) {
    return {
      success: false,
      code: "invalid_body",
      message: "종목 검색 응답 형식이 올바르지 않습니다.",
    };
  }
  if (value.schemaVersion !== MARKET_QUOTE_SCHEMA_VERSION) {
    return {
      success: false,
      code: "unsupported_version",
      message: "지원하지 않는 종목 검색 응답 버전입니다.",
    };
  }
  const query = normalizedSearchQuery(value.query);
  if (
    value.source !== MARKET_QUOTE_SOURCE ||
    !query ||
    query !== value.query ||
    !Array.isArray(value.results) ||
    value.results.length > MARKET_INSTRUMENT_SEARCH_MAX_RESULTS
  ) {
    return invalidRequest("종목 검색 응답 값이 올바르지 않습니다.");
  }

  const results: MarketInstrumentSearchResultV1[] = [];
  const tickers = new Set<string>();
  for (const result of value.results) {
    if (
      !isObject(result) ||
      !hasOnlyKeys(
        result,
        new Set(["canonicalTicker", "name", "exchange", "assetKind"]),
      ) ||
      !isTicker(result.canonicalTicker) ||
      !/^\d{6}\.(?:KS|KQ)$/.test(result.canonicalTicker) ||
      typeof result.name !== "string" ||
      result.name.length === 0 ||
      result.name.length > 120 ||
      !/[가-힣]/.test(result.name) ||
      (result.exchange !== "KOSPI" && result.exchange !== "KOSDAQ") ||
      (result.assetKind !== "stock" && result.assetKind !== "etf") ||
      tickers.has(result.canonicalTicker)
    ) {
      return invalidRequest("종목 검색 응답 항목이 올바르지 않습니다.");
    }
    tickers.add(result.canonicalTicker);
    results.push({
      canonicalTicker: result.canonicalTicker,
      name: result.name,
      exchange: result.exchange,
      assetKind: result.assetKind,
    });
  }

  return {
    success: true,
    data: {
      schemaVersion: MARKET_QUOTE_SCHEMA_VERSION,
      source: MARKET_QUOTE_SOURCE,
      query,
      results,
    },
  };
}
